import { ChangeEvent, FormEvent, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { InputLabel, Message } from '@/components';
import {
  validateEmail,
  validateName,
  validatePhone,
} from '@/utils/formValidations';
import { useMutation } from '@tanstack/react-query';
import { validateMessage } from './validations';
import styles from './ContactForm.module.scss';
import { sendContact } from './sendContact';

const initialFormData = {
  name: '',
  phone: '',
  email: '',
  message: '',
};

const ContactForm = () => {
  const { t } = useTranslation('contact');

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormData);

  const clearForm = () => setFormData(initialFormData);

  const contactMutation = useMutation({
    mutationFn: sendContact,
    onSuccess: () => clearForm(),
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: '',
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nameError = validateName(formData.name, {
      required: t('form.errors.nameRequired'),
      invalid: t('form.errors.invalidName'),
      lengthExceeded: t('form.errors.nameLengthExceeded'),
    });
    const phoneError = validatePhone(formData.phone, {
      required: t('form.errors.phoneRequired'),
      invalid: t('form.errors.invalidPhone'),
      lengthExceeded: t('form.errors.phoneLengthExceeded'),
    });
    const emailError = validateEmail(formData.email, {
      required: t('form.errors.emailRequired'),
      invalid: t('form.errors.invalidEmail'),
    });
    const messageError = validateMessage(formData.message);

    setFormErrors({
      name: nameError,
      phone: phoneError,
      email: emailError,
      message: messageError,
    });

    if (!nameError && !phoneError && !emailError && !messageError) {
      contactMutation.mutate(formData);
    }
  };

  const submitButtonText = contactMutation.isPending
    ? t('form.sendingButton')
    : t('form.submitButton');

  return (
    <form onSubmit={handleSubmit} className={styles.contactForm}>
      <div className={styles.fieldContainer}>
        <InputLabel htmlFor="name" label={t('form.nameFieldLabel')} />
        <InputText
          name="name"
          value={formData.name}
          onChange={(e) => handleInputChange(e, e.target.name)}
          className={classNames(
            styles.field,
            formErrors.name && styles.invalid
          )}
          placeholder={t('form.nameFieldPlaceholder')}
          type="text"
        />
        {formErrors.name ? (
          <p className={styles.errorMessage}>{formErrors.name}</p>
        ) : null}
      </div>
      <div className={styles.fieldContainer}>
        <InputLabel htmlFor="phone" label={t('form.phoneFieldLabel')} />
        <InputText
          name="phone"
          value={formData.phone}
          onChange={(e) => handleInputChange(e, e.target.name)}
          className={classNames(
            styles.field,
            formErrors.phone && styles.invalid
          )}
          placeholder={t('form.phoneFieldPlaceholder')}
          type="tel"
        />
        {formErrors.phone ? (
          <p className={styles.errorMessage}>{formErrors.phone}</p>
        ) : null}
      </div>
      <div className={styles.fieldContainer}>
        <InputLabel htmlFor="email" label={t('form.emailFieldLabel')} />
        <InputText
          name="email"
          value={formData.email}
          onChange={(e) => handleInputChange(e, e.target.name)}
          className={classNames(
            styles.field,
            formErrors.email && styles.invalid
          )}
          placeholder={t('form.emailFieldPlaceholder')}
          type="email"
        />
        {formErrors.email ? (
          <p className={styles.errorMessage}>{formErrors.email}</p>
        ) : null}
      </div>
      <div className={styles.fieldContainer}>
        <InputLabel htmlFor="message" label={t('form.messageFieldLabel')} />
        <InputTextarea
          name="message"
          value={formData.message}
          placeholder={t('form.messageFieldPlaceholder')}
          onChange={(e) => handleInputChange(e, e.target.name)}
          rows={5}
          className={classNames(
            styles.field,
            formErrors.message && styles.invalid
          )}
        />
        {formErrors.message ? (
          <p className={styles.errorMessage}>{formErrors.message}</p>
        ) : null}
      </div>
      {contactMutation.isSuccess && (
        <Message
          title={t('messageSentTitle')}
          messageText={t('messageSentText')}
          icon={faCircleCheck}
        />
      )}
      <Button
        type="submit"
        disabled={contactMutation.isPending || contactMutation.isError}
        className={styles.submitButton}
      >
        {contactMutation.isError ? 'Error' : submitButtonText}
      </Button>
    </form>
  );
};

export default ContactForm;
